import React, { useState } from 'react';

import { DepartmentPermissionTagInfo } from '@vlabs/monokit/entities/permission-tags';
import { useGetUser, useGetDepartments } from '@vlabs/api/artemy/client';
import type { UserId } from '@vlabs/api/artemy/model';
import { buildTestid } from '@vlabs/monokit/shared/lib/utils';
import { FoldableEntity } from '@vlabs/monokit/shared/ui/foldable-entity';

import { useUserPermissionTagListTranslation } from './i18n';
import st from './UserPermissionTagList.module.sass';

interface UserPermissionTagListProps {
  userId: UserId;
}

const displayName = 'UserPermissionTagList';
const testid = buildTestid(displayName);

function UserPermissionTagList({
  userId,
}: UserPermissionTagListProps): React.JSX.Element {
  const { t } = useUserPermissionTagListTranslation();
  const [isTagsOpen, setIsTagsOpen] = useState<boolean>(false);

  const { data: user } = useGetUser(userId);
  const { data: departments } = useGetDepartments({ department_ids: user?.department_ids }, {
    query: {
      enabled: (user?.department_ids?.length ?? 0) > 0,
    },
  });

  const permissionTags = departments?.departments?.flatMap(
    (department) => department.permission_tags?.filter((permissionTag) => permissionTag.open) ?? [],
  ) ?? [];

  return (
    <FoldableEntity
      heading={t('title')}
      {...testid()}
    >
      {permissionTags.length > 0 && (
        <React.Fragment>
          <button
            aria-label="Toggle expand button"
            className={st.Button}
            onClick={() => setIsTagsOpen((prev) => !prev)}
            type="button"
            {...testid('Toggle')}
          >
            {t('button', { context: isTagsOpen ? 'collapse' : 'expand' })}
          </button>

          {permissionTags.map((permissionTag) => (
            <DepartmentPermissionTagInfo
              isOpen={isTagsOpen}
              key={permissionTag.permission_tag}
              permissionTag={permissionTag.permission_tag}
              {...testid()}
            />
          ))}
        </React.Fragment>
      )}
    </FoldableEntity>
  );
}

UserPermissionTagList.displayName = displayName;

export { UserPermissionTagList };
